import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { GeneralConstants } from 'app/constants/general-constants';
import { Comment } from 'app/model/comment';
import { ListOptions } from 'app/model/list-options';
import { RestfulModel } from 'app/model/restful-model';
import { SeamPattern } from 'app/model/seam-pattern';
import { SeamRequest } from 'app/model/seam-request';
import { SeamRequestFilter } from 'app/model/seam-request-filter';
import { seamCommentService } from 'app/services/comment.service';
import { overallMetricService } from 'app/services/metrics/overall-metrics.service';
import { storeMetricService } from 'app/services/metrics/store-metrics.service';
import { seamPatternService } from 'app/services/seam-pattern.service';
import { seamRequestService } from 'app/services/seam-request.service';
import { AppThunk } from 'app/store';
import { ModelWithoutId } from 'app/type';

import { setOverallMetrics, setSeamComments, setSeamPatterns, setStoreGraphMetrics, setStoreMetrics } from './slice';

export const fetchSeamRequests =
    (filter?: SeamRequestFilter): AppThunk<Observable<SeamRequest[]>> =>
    () => {
        const opts: ListOptions = { pageSize: 1000 };

        if (!filter) {
            return seamRequestService.listAll(opts);
        }

        // initializes to empty object if it is undefined
        opts.queryFilters = {
            ...opts.queryFilters,
        };

        if (filter.status) {
            opts.queryFilters.status = 'string' === typeof filter.status ? [filter.status] : filter.status;
        }

        if (filter.my) {
            opts.queryFilters.my = filter.my;
        }

        if (filter.assignee) {
            opts.queryFilters.assignee = filter.assignee.id;
        }

        if (filter.tuner) {
            opts.queryFilters.tuner = filter.tuner.id;
        }

        if (filter.term) {
            opts.queryFilters.term = filter.term;
        }

        if (filter.cris) {
            opts.queryFilters.cris = filter.cris;
        }

        if (filter.createdAfter) {
            opts.queryFilters.createdAfter = filter.createdAfter;
        }
        if (filter.createdBefore) {
            opts.queryFilters.createdBefore = filter.createdBefore;
        }
        if (filter.preprodDeployAfter) {
            opts.queryFilters.preprodDeployAfter = filter.preprodDeployAfter;
        }
        if (filter.preprodDeployBefore) {
            opts.queryFilters.preprodDeployBefore = filter.preprodDeployBefore;
        }
        if (filter.prodDeployAfter) {
            opts.queryFilters.prodDeployAfter = filter.prodDeployAfter;
        }
        if (filter.prodDeployBefore) {
            opts.queryFilters.prodDeployBefore = filter.prodDeployBefore;
        }

        opts.download = filter.download;

        return seamRequestService.listAll(opts);
    };

export const loadSeamRequest =
    (id: string): AppThunk<Observable<SeamRequest>> =>
    () =>
        seamRequestService.get(id);

export const saveSeamRequest =
    (request: SeamRequest): AppThunk<Observable<SeamRequest>> =>
    () =>
        seamRequestService.save(request);

export const loadSeamComments =
    (request: RestfulModel): AppThunk<Observable<Comment[]>> =>
    () =>
        seamCommentService.listAll({}, request);

export const addSeamComment =
    (comment: string, request: SeamRequest): AppThunk<Observable<Comment>> =>
    (dispatch, state) =>
        seamCommentService.save({ comment }, request).pipe(
            tap((com) => {
                dispatch(setSeamComments([...state().seam.seamComments, com]));
            }),
        );

export const loadSeamPatterns =
    (request: SeamRequest): AppThunk<Observable<SeamPattern[]>> =>
    () =>
        seamPatternService.listAll({}, request);

export const saveNewPattern =
    (patternData: ModelWithoutId<SeamPattern>, request: SeamRequest): AppThunk<Observable<SeamPattern>> =>
    (dispatch, state) =>
        seamPatternService.save(patternData, request).pipe(
            tap((pattern) => {
                if (!patternData.id) {
                    // new pattern
                    dispatch(setSeamPatterns([pattern, ...(state().seam.seamPatterns || [])]));

                    return;
                }
                // updated pattern
                dispatch(
                    setSeamPatterns(
                        (state().seam.seamPatterns || []).map((pat) => (pat.id === pattern.id ? pattern : pat)),
                    ),
                );
            }),
        );

export const deletePattern =
    (patternData: SeamPattern, request: SeamRequest): AppThunk<Observable<void>> =>
    (dispatch, state) =>
        seamPatternService.delete(patternData, request).pipe(
            tap(() =>
                // updated pattern
                dispatch(setSeamPatterns((state().seam.seamPatterns || []).filter((pat) => pat.id !== patternData.id))),
            ),
        );

export const loadOverallMetrics = (): AppThunk<unknown> => (dispatch) =>
    overallMetricService.listAll({ pageSize: GeneralConstants.PAGE_SIZE_HUGE }).subscribe((metrics) => {
        dispatch(setOverallMetrics(metrics));
    });

export const loadStoreMetrics =
    (date: string): AppThunk<unknown> =>
    (dispatch) =>
        storeMetricService
            .listAll({
                queryFilters: {
                    date,
                },
                pageSize: GeneralConstants.PAGE_SIZE_HUGE,
            })
            .subscribe((metrics) => {
                dispatch(setStoreMetrics(metrics));
            });

export const loadStoreGraphData =
    (store: number): AppThunk<unknown> =>
    (dispatch) => {
        dispatch(setStoreGraphMetrics(undefined));

        return storeMetricService
            .listAll({
                queryFilters: {
                    store,
                },
                pageSize: GeneralConstants.PAGE_SIZE_HUGE,
            })
            .subscribe((metrics) => {
                dispatch(setStoreGraphMetrics(metrics));
            });
    };
